import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface DonationModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface Church {
  id: string;
  name: string;
  address: string;
}

export default function DonationModal({ onClose, onSuccess }: DonationModalProps) {
  const { user } = useAuth();
  const [churches, setChurches] = useState<Church[]>([]);
  const [selectedChurch, setSelectedChurch] = useState('');
  const [donationType, setDonationType] = useState<'tithe' | 'offering' | 'donation'>('offering');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [talentBalance, setTalentBalance] = useState(0);

  useEffect(() => {
    loadChurches();
    loadTalentBalance();
  }, []);

  const loadChurches = async () => {
    try {
      const { data, error } = await supabase
        .from('churches')
        .select('id, name, location')
        .eq('is_approved', true)
        .order('name');

      if (error) throw error;
      setChurches(data || []);
    } catch (error) {
      console.error('교회 목록 로드 실패:', error);
    }
  };

  const loadTalentBalance = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('talent_points')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setTalentBalance(data?.balance || 0);
    } catch (error) {
      console.error('달란트 잔액 로드 실패:', error);
    }
  };

  const presetAmounts = [10000, 30000, 50000, 100000, 300000, 500000];

  const getDonationTypeLabel = () => {
    switch (donationType) {
      case 'tithe':
        return '십일조';
      case 'offering':
        return '헌금';
      case 'donation':
        return '기부';
      default:
        return '';
    }
  };

  const handleDonation = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!selectedChurch) {
      alert('교회를 선택해주세요.');
      return;
    }

    const donationAmount = parseInt(amount);
    if (!donationAmount || donationAmount < 1000) {
      alert('최소 금액은 1,000 달란트입니다.');
      return;
    }

    if (donationAmount > talentBalance) {
      alert('보유한 달란트가 부족합니다.');
      return;
    }

    setLoading(true);

    try {
      // 달란트 차감
      const { data: currentPoints } = await supabase
        .from('talent_points')
        .select('balance, total_spent')
        .eq('user_id', user.id)
        .single();

      if (currentPoints) {
        await supabase
          .from('talent_points')
          .update({
            balance: currentPoints.balance - donationAmount,
            total_spent: currentPoints.total_spent + donationAmount,
          })
          .eq('user_id', user.id);
      }

      // 거래 내역 생성
      const church = churches.find(c => c.id === selectedChurch);
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: -donationAmount,
          type: 'spend',
          description: `${getDonationTypeLabel()} - ${church?.name}${message ? ': ' + message : ''}`,
          reference_type: 'donation',
          reference_id: selectedChurch,
        });

      alert(`${getDonationTypeLabel()}이 완료되었습니다.\n${donationAmount.toLocaleString()} 달란트가 사용되었습니다.`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('기부 실패:', error);
      alert('처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">기부 · 헌금</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-teal-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-800 font-medium">보유 달란트</span>
              <span className="text-lg font-bold text-teal-600">
                <i className="ri-coin-line mr-1"></i>
                {talentBalance.toLocaleString()}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              유형 선택
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setDonationType('tithe')}
                className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  donationType === 'tithe'
                    ? 'border-teal-600 bg-teal-50 text-teal-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                십일조
              </button>
              <button
                onClick={() => setDonationType('offering')}
                className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  donationType === 'offering'
                    ? 'border-teal-600 bg-teal-50 text-teal-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                헌금
              </button>
              <button
                onClick={() => setDonationType('donation')}
                className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  donationType === 'donation'
                    ? 'border-teal-600 bg-teal-50 text-teal-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                기부
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              교회 선택
            </label>
            <select
              value={selectedChurch}
              onChange={(e) => setSelectedChurch(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
            >
              <option value="">교회를 선택하세요</option>
              {churches.map((church) => (
                <option key={church.id} value={church.id}>
                  {church.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              금액 선택
            </label>
            <div className="grid grid-cols-3 gap-3">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setAmount(preset.toString())}
                  className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    amount === preset.toString()
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {(preset / 10000).toFixed(0)}만
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              직접 입력
            </label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="금액을 입력하세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                min="1000"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                달란트
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              최소 1,000 달란트
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              메시지 (선택)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="전하고 싶은 메시지를 입력하세요"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
              rows={3}
              maxLength={500}
            />
            <p className="mt-1 text-xs text-gray-500 text-right">
              {message.length}/500
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              onClick={handleDonation}
              disabled={loading || !selectedChurch || !amount || parseInt(amount) < 1000}
              className="flex-1 py-3 px-6 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '완료'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
